import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';

const ArtistProfileCreation = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    artistName: '',
    bio: '',
    genres: [],
    budget: '',
    paymentMethod: '',
    logoUrl: '',
    socialLinks: {
      instagram: '',
      youtube: '',
      spotify: '',
      twitter: ''
    },
    businessRegistration: false,
    taxId: ''
  });

  const [bioCharCount, setBioCharCount] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const maxBioLength = 500;

  const musicGenres = [
    { value: 'pop', label: 'Pop' },
    { value: 'rock', label: 'Rock' },
    { value: 'hip-hop', label: 'Hip-Hop / Rap' },
    { value: 'electronic', label: 'Elektronik' },
    { value: 'jazz', label: 'Jazz' },
    { value: 'classical', label: 'Klasik' },
    { value: 'rnb', label: 'R&B' },
    { value: 'indie', label: 'Indie / Alternative' },
    { value: 'folk', label: 'Folk / Akustik' },
    { value: 'metal', label: 'Metal' },
    { value: 'turkish-pop', label: 'Türkçe Pop' },
    { value: 'arabesk', label: 'Arabesk' }
  ];

  const paymentMethods = [
    { value: 'credit-card', label: 'Kredi Kartı' },
    { value: 'bank-transfer', label: 'Banka Havalesi' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'crypto', label: 'Kripto Para' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    calculateCompletion();
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev?.socialLinks,
        [platform]: value
      }
    }));
    calculateCompletion();
  };

  const handleBioChange = (e) => {
    const value = e?.target?.value || '';
    if (value?.length <= maxBioLength) {
      setBioCharCount(value?.length);
      handleInputChange('bio', value);
    }
  };

  const calculateCompletion = () => {
    const fields = [
      formData?.artistName,
      formData?.bio,
      formData?.genres?.length > 0,
      formData?.budget,
      formData?.paymentMethod
    ];
    const completed = fields?.filter(Boolean)?.length;
    const percentage = Math.round((completed / fields?.length) * 100);
    setCompletionPercentage(percentage);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Artist Profile Data:', formData);
    navigate('/artist-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="artist" />
      
      <main className="container mx-auto px-4 py-6 md:py-8 pb-20 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Geri Dön</span>
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Sanatçı Profili Oluştur
              </h1>
              <p className="text-muted-foreground">
                Müzik kariyerinizi tanıtmak için profilinizi tamamlayın
              </p>
            </div>
            
            <div className="hidden md:flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3">
              <div className="relative w-16 h-16">
                <svg className="transform -rotate-90 w-16 h-16">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="var(--color-muted)"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="var(--color-primary)"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`}
                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - completionPercentage / 100)}`}
                    className="transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-semibold">{completionPercentage}%</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium">Tamamlanma</p>
                <p className="text-xs text-muted-foreground">Profil Durumu</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="User" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Temel Bilgiler</h2>
                <p className="text-sm text-muted-foreground">Sanatçı adınız ve biyografiniz</p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Sanatçı Adı"
                placeholder="Örn: Ahmet Yılmaz"
                value={formData?.artistName}
                onChange={(e) => handleInputChange('artistName', e?.target?.value)}
                required
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Profesyonel Biyografi
                  <span className="text-destructive ml-1">*</span>
                </label>
                <textarea
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Müzik kariyerinizi, tarzınızı ve başarılarınızı anlatın..."
                  value={formData?.bio}
                  onChange={handleBioChange}
                  required
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Profilinizi tanıtan kısa bir açıklama yazın</span>
                  <span className={bioCharCount >= maxBioLength ? 'text-error' : ''}>
                    {bioCharCount} / {maxBioLength}
                  </span>
                </div>
              </div>

              <Select
                label="Müzik Türleri"
                placeholder="Müzik türlerinizi seçin"
                options={musicGenres}
                value={formData?.genres}
                onChange={(value) => handleInputChange('genres', value)}
                multiple
                searchable
                description="Birden fazla tür seçebilirsiniz"
                required
              />
            </div>
          </div>

          {/* Budget Configuration */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Bütçe Ayarları</h2>
                <p className="text-sm text-muted-foreground">Kampanya harcama limitleri ve ödeme tercihleri</p>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Kampanya Bütçesi (₺)"
                type="number"
                placeholder="Örn: 10000"
                value={formData?.budget}
                onChange={(e) => handleInputChange('budget', e?.target?.value)}
                description="Aylık kampanya harcama limitiniz"
                required
              />

              <Select
                label="Tercih Edilen Ödeme Yöntemi"
                placeholder="Ödeme yöntemi seçin"
                options={paymentMethods}
                value={formData?.paymentMethod}
                onChange={(value) => handleInputChange('paymentMethod', value)}
                required
              />
            </div>
          </div>

          {/* Social Media Links */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Share2" size={20} color="var(--color-secondary)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Sosyal Medya Bağlantıları</h2>
                <p className="text-sm text-muted-foreground">Müzik platformları ve sosyal medya hesaplarınız</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Instagram"
                placeholder="https://instagram.com/kullaniciadi"
                value={formData?.socialLinks?.instagram}
                onChange={(e) => handleSocialLinkChange('instagram', e?.target?.value)}
              />

              <Input
                label="YouTube"
                placeholder="https://youtube.com/@kullaniciadi"
                value={formData?.socialLinks?.youtube}
                onChange={(e) => handleSocialLinkChange('youtube', e?.target?.value)}
              />

              <Input
                label="Spotify"
                placeholder="https://open.spotify.com/artist/..."
                value={formData?.socialLinks?.spotify}
                onChange={(e) => handleSocialLinkChange('spotify', e?.target?.value)}
              />

              <Input
                label="Twitter / X"
                placeholder="https://twitter.com/kullaniciadi"
                value={formData?.socialLinks?.twitter}
                onChange={(e) => handleSocialLinkChange('twitter', e?.target?.value)}
              />
            </div>
          </div>

          {/* Logo Upload */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Image" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Profil Görseli</h2>
                <p className="text-sm text-muted-foreground">Logo veya profil fotoğrafınız</p>
              </div>
            </div>

            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-smooth cursor-pointer">
              <Icon name="Upload" size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">Görsel yüklemek için tıklayın</p>
              <p className="text-xs text-muted-foreground">PNG, JPG veya WEBP (Maks. 5MB)</p>
            </div>
          </div>

          {/* Verification */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="ShieldCheck" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Doğrulama</h2>
                <p className="text-sm text-muted-foreground">İş kaydı ve vergi bilgileri</p>
              </div>
            </div>

            <div className="space-y-4">
              <Checkbox
                label="İşletme Kaydım Var"
                description="Türkiye'de kayıtlı bir işletme olarak faaliyet gösteriyorum"
                checked={formData?.businessRegistration}
                onChange={(e) => handleInputChange('businessRegistration', e?.target?.checked)}
              />

              {formData?.businessRegistration && (
                <Input
                  label="Vergi Kimlik Numarası"
                  placeholder="Vergi numaranızı girin"
                  value={formData?.taxId}
                  onChange={(e) => handleInputChange('taxId', e?.target?.value)}
                  description="Fatura düzenleme için gereklidir"
                />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row gap-3 md:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="md:w-auto"
            >
              İptal
            </Button>
            <Button
              type="submit"
              iconName="Check"
              className="md:w-auto"
            >
              Profili Kaydet
            </Button>
          </div>
        </form>
      </main>

      <MobileNav userRole="artist" />
    </div>
  );
};

export default ArtistProfileCreation;